import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Autocomplete,
  CircularProgress,
  IconButton,
  Stack,
} from '@mui/material';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import chatApi from './ChatApi';
import { useDispatch } from 'react-redux';
import { commonFileUpload } from '../../redux/features/assetManagementSlice';

const GroupCreation = ({ selectChat, getChatList }) => {
  const [avatar, setAvatar] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [members, setMembers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const dispatch = useDispatch();

  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [showUrl, setShowUrl] = useState(null);
  const [groupNameRerror, setGroupNameError] = useState(false);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    fetchEmployees('', true);
  }, []);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatar(URL.createObjectURL(file));
    }
  };

  const uploadFile = async (e, dispatch) => {
    try {
      const file = e.target.files[0];
      if (file) {
        setShowUrl(URL.createObjectURL(file));
      }
      const formData = new FormData();
      formData.append('uploads', file);
      const response = await dispatch(commonFileUpload(formData));
      setAvatar(response.payload?.data?.filename || null);
    } catch (error) {
      console.error('File upload failed:', error);
      setAvatar(null);
    }
  };

  const fetchEmployees = async (search = '', reset = true) => {
    try {
      setLoadingEmployees(true);
      const res = await chatApi.getAllEmployeesList({
        page: 1,
        limit: 50,
        search,
      });

      const newData = res?.data ?? [];
      setEmployees(reset ? newData : [...employees, ...newData]);
    } catch (error) {
      console.error('❌ Error fetching employees:', error);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const createNewGroup = async () => {
    try {
      if (!groupName.trim()) {
        setGroupNameError(true);
        return;
      } else {
        setGroupNameError(false);
      }
      setCreatingGroup(true);

      const memberIds = members.map((member) => member.userID);

      const res = await chatApi.createGroup(groupName, memberIds, {
        profile_pic: avatar,
        description,
      });

      const CreatedchatRoomId = res?.data?.chat_room_id;
      if (!CreatedchatRoomId) return;

      const updatedChats = await getChatList();
      const exists = updatedChats.find((c) => c.chat_room_id === CreatedchatRoomId);

      if (exists) {
        selectChat(CreatedchatRoomId);
      }
    } catch (error) {
      console.error('❌ Error creating group:', error);
    } finally {
      setCreatingGroup(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="flex-start" minHeight="100vh" bgcolor="#f5f5f5">
      <Card
        sx={{
          width: 420,
          height: '107vh',
          paddingBottom: 25,
          borderRadius: 3,
          boxShadow: 4,
          overflow: 'hidden',
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            overflowY: 'auto',
          }}
        >
          {/* Avatar Upload */}
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Box
              sx={{
                position: 'relative',
                width: 96,
                height: 96,
                borderRadius: '50%',
                border: '2px dashed #1976d2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <Avatar src={showUrl} sx={{ width: 88, height: 88 }} />
              <label htmlFor="avatar-upload">
                <input
                  hidden
                  accept="image/*"
                  id="avatar-upload"
                  type="file"
                  onChange={(e) => uploadFile(e, dispatch)}
                />
                <AddAPhotoIcon
                  color="primary"
                  sx={{
                    position: 'absolute',
                    bottom: -4,
                    fontSize: 40,
                    cursor: 'pointer',
                    right: -4,
                    bgcolor: '#fff',
                    borderRadius: '50%',
                    p: 0.5,
                  }}
                />
              </label>
            </Box>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Upload Group Profile Picture
            </Typography>
          </Box>

          {/* Group Name */}
          <TextField
            sx={{ mb: 2 }}
            label="Group Name"
            fullWidth
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            error={groupNameRerror}
            helperText={groupNameRerror ? 'Group name is required' : ''}
          />

          {/* Description */}
          <TextField
            sx={{ mb: 2 }}
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          {/* Members Select */}
          {/* <Autocomplete
            multiple
            disableCloseOnSelect
            options={employees}
            loading={loadingEmployees}
            getOptionLabel={(option) => option.name}
            value={members}
            onChange={(e, newValue) => setMembers(newValue)}
            filterSelectedOptions
            onInputChange={(event, value, reason) => {
              if (reason === 'input') {
                setSearchText(value);
                fetchEmployees(value, true);
              }
            }}

            renderInput={(params) => (
              <TextField
                {...params}
                label="Search & Select Members"
                placeholder="Type to search"
                margin="normal"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingEmployees && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          /> */}

          <Autocomplete
            options={employees}
            loading={loadingEmployees}
            getOptionLabel={(option) => option.name || ''}
            filterOptions={(options) => options} // 🔥 IMPORTANT for server-side
            value={null}
            inputValue={inputValue}
            onChange={(e, value) => {
              if (!value) return;

              setMembers((prev) => {
                if (prev.some((m) => m.userID === value.userID)) return prev;
                return [...prev, value];
              });

              setInputValue(''); // ✅ clear input after select
            }}
            onInputChange={(event, value, reason) => {
              if (reason === 'input') {
                setInputValue(value);
                setSearchText(value);
                fetchEmployees(value, true);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Members"
                placeholder="Type name..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingEmployees && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Submit Button */}
          <Button
            onClick={createNewGroup}
            
            fullWidth
            disabled={creatingGroup}
            sx={{
              mt: 3,
              borderRadius: 2,
              color:'white',
              backgroundColor: '#620a99',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#45036e',
                boxShadow: 'none',
              },
            }}
          >
            {creatingGroup ? <CircularProgress size={24} color="inherit" /> : 'Create Group'}
          </Button>

          <Box
            sx={{
              display: members.length === 0 ? 'none' : 'block',
              maxHeight: 250,
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 1,
              mt: 2,
              mb: 2,
            }}
          >
            {members.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No members selected
              </Typography>
            ) : (
              members.map((member) => (
                <Stack
                  key={member.userID}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    p: 0.5,
                    borderRadius: 1,
                    '&:hover': { bgcolor: '#f5f5f5' },
                  }}
                >
                  <Avatar sx={{ width: 28, height: 28 }}>{member.name?.charAt(0)}</Avatar>

                  <Typography flex={1} fontSize={14}>
                    {member.name}
                  </Typography>

                  <IconButton
                    size="small"
                    onClick={() => setMembers((prev) => prev.filter((m) => m.userID !== member.userID))}
                  >
                    ✕
                  </IconButton>
                </Stack>
              ))
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GroupCreation;
